import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AiTemplate, AiPrompt, AiTemplateState, AiPromptState } from '@/types/ai.types';

interface CombinedAiState {
  templates: AiTemplateState;
  prompts: AiPromptState;
}

const initialState: CombinedAiState = {
  templates: { templates: [], currentTemplate: null, loading: false, error: null },
  prompts: { prompts: [], currentPrompt: null, loading: false, error: null },
};

const aiTemplateSlice = createSlice({
  name: 'aiTemplate',
  initialState,
  reducers: {
    // Templates
    fetchAiTemplatesRequest(state) {
      state.templates.loading = true;
      state.templates.error = null;
    },
    fetchAiTemplatesSuccess(state, action: PayloadAction<AiTemplate[]>) {
      state.templates.loading = false;
      state.templates.templates = action.payload;
    },
    fetchAiTemplatesFailure(state, action: PayloadAction<string>) {
      state.templates.loading = false;
      state.templates.error = action.payload;
    },

    fetchAiTemplateRequest(state, _action: PayloadAction<string>) {
      state.templates.loading = true;
      state.templates.error = null;
    },
    fetchAiTemplateSuccess(state, action: PayloadAction<AiTemplate>) {
      state.templates.loading = false;
      state.templates.currentTemplate = action.payload;
    },
    fetchAiTemplateFailure(state, action: PayloadAction<string>) {
      state.templates.loading = false;
      state.templates.error = action.payload;
    },

    createAiTemplateRequest(state, _action: PayloadAction<Record<string, unknown>>) {
      state.templates.loading = true;
      state.templates.error = null;
    },
    createAiTemplateSuccess(state, action: PayloadAction<AiTemplate>) {
      state.templates.loading = false;
      state.templates.templates.push(action.payload);
    },
    createAiTemplateFailure(state, action: PayloadAction<string>) {
      state.templates.loading = false;
      state.templates.error = action.payload;
    },

    updateAiTemplateRequest(state, _action: PayloadAction<{ id: string; data: Record<string, unknown> }>) {
      state.templates.loading = true;
      state.templates.error = null;
    },
    updateAiTemplateSuccess(state, action: PayloadAction<AiTemplate>) {
      state.templates.loading = false;
      const idx = state.templates.templates.findIndex((t) => t._id === action.payload._id);
      if (idx >= 0) state.templates.templates[idx] = action.payload;
    },
    updateAiTemplateFailure(state, action: PayloadAction<string>) {
      state.templates.loading = false;
      state.templates.error = action.payload;
    },

    deleteAiTemplateRequest(state, _action: PayloadAction<string>) {
      state.templates.loading = true;
      state.templates.error = null;
    },
    deleteAiTemplateSuccess(state, action: PayloadAction<string>) {
      state.templates.loading = false;
      state.templates.templates = state.templates.templates.filter((t) => t._id !== action.payload);
    },
    deleteAiTemplateFailure(state, action: PayloadAction<string>) {
      state.templates.loading = false;
      state.templates.error = action.payload;
    },

    clearCurrentTemplate(state) {
      state.templates.currentTemplate = null;
    },

    // Prompts
    fetchPromptsRequest(state) {
      state.prompts.loading = true;
      state.prompts.error = null;
    },
    fetchPromptsSuccess(state, action: PayloadAction<AiPrompt[]>) {
      state.prompts.loading = false;
      state.prompts.prompts = action.payload;
    },
    fetchPromptsFailure(state, action: PayloadAction<string>) {
      state.prompts.loading = false;
      state.prompts.error = action.payload;
    },

    fetchPromptRequest(state, _action: PayloadAction<string>) {
      state.prompts.loading = true;
      state.prompts.error = null;
    },
    fetchPromptSuccess(state, action: PayloadAction<AiPrompt>) {
      state.prompts.loading = false;
      state.prompts.currentPrompt = action.payload;
    },
    fetchPromptFailure(state, action: PayloadAction<string>) {
      state.prompts.loading = false;
      state.prompts.error = action.payload;
    },

    createPromptRequest(state, _action: PayloadAction<Record<string, unknown>>) {
      state.prompts.loading = true;
      state.prompts.error = null;
    },
    createPromptSuccess(state, action: PayloadAction<AiPrompt>) {
      state.prompts.loading = false;
      state.prompts.prompts.push(action.payload);
    },
    createPromptFailure(state, action: PayloadAction<string>) {
      state.prompts.loading = false;
      state.prompts.error = action.payload;
    },

    updatePromptRequest(state, _action: PayloadAction<{ id: string; data: Record<string, unknown> }>) {
      state.prompts.loading = true;
      state.prompts.error = null;
    },
    updatePromptSuccess(state, action: PayloadAction<AiPrompt>) {
      state.prompts.loading = false;
      const idx = state.prompts.prompts.findIndex((p) => p._id === action.payload._id);
      if (idx >= 0) state.prompts.prompts[idx] = action.payload;
    },
    updatePromptFailure(state, action: PayloadAction<string>) {
      state.prompts.loading = false;
      state.prompts.error = action.payload;
    },

    deletePromptRequest(state, _action: PayloadAction<string>) {
      state.prompts.loading = true;
      state.prompts.error = null;
    },
    deletePromptSuccess(state, action: PayloadAction<string>) {
      state.prompts.loading = false;
      state.prompts.prompts = state.prompts.prompts.filter((p) => p._id !== action.payload);
    },
    deletePromptFailure(state, action: PayloadAction<string>) {
      state.prompts.loading = false;
      state.prompts.error = action.payload;
    },

    clearCurrentPrompt(state) {
      state.prompts.currentPrompt = null;
    },
  },
});

export const {
  fetchAiTemplatesRequest,
  fetchAiTemplatesSuccess,
  fetchAiTemplatesFailure,
  fetchAiTemplateRequest,
  fetchAiTemplateSuccess,
  fetchAiTemplateFailure,
  createAiTemplateRequest,
  createAiTemplateSuccess,
  createAiTemplateFailure,
  updateAiTemplateRequest,
  updateAiTemplateSuccess,
  updateAiTemplateFailure,
  deleteAiTemplateRequest,
  deleteAiTemplateSuccess,
  deleteAiTemplateFailure,
  clearCurrentTemplate,
  fetchPromptsRequest,
  fetchPromptsSuccess,
  fetchPromptsFailure,
  fetchPromptRequest,
  fetchPromptSuccess,
  fetchPromptFailure,
  createPromptRequest,
  createPromptSuccess,
  createPromptFailure,
  updatePromptRequest,
  updatePromptSuccess,
  updatePromptFailure,
  deletePromptRequest,
  deletePromptSuccess,
  deletePromptFailure,
  clearCurrentPrompt,
} = aiTemplateSlice.actions;

export default aiTemplateSlice.reducer;
